import { attr, url as escapeUrl } from "./escape.js";

export const IMAGE_OPTIMIZER_BASE_URL = "https://img.stero.ai";
export const IMAGE_OPTIMIZER_WIDTHS = [480, 768, 1024, 1280, 1920] as const;
const DEFAULT_FALLBACK_WIDTH = 1024;

export type ImageOptimizerFormat = "avif" | "webp" | "jpeg";

function extractSrc(tag: string): string | null {
  const match = tag.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
  return match?.[1] ?? null;
}

function encodeImagePath(pathname: string): string {
  return pathname
    .replace(/^\/+/, "")
    .split("/")
    .filter(Boolean)
    .map((segment) => encodeURIComponent(segment))
    .join("/");
}

export function getOptimizableImagePath(originalUrl: string): string | null {
  if (!originalUrl) return null;

  try {
    const parsed = new URL(originalUrl);
    if (parsed.pathname.startsWith("/media/") || parsed.pathname.startsWith("/assets/")) {
      return parsed.pathname;
    }

    if (/\.r2\.cloudflarestorage\.com$/i.test(parsed.hostname)) {
      return parsed.pathname;
    }

    return null;
  } catch {
  return originalUrl.startsWith("/media/") || originalUrl.startsWith("/assets/")
    ? originalUrl
    : null;
}
}

export function optimizeImageUrl(
  originalUrl: string,
  options: { width: number; format: ImageOptimizerFormat },
): string | null {
  const optimizablePath = getOptimizableImagePath(originalUrl);
  if (!optimizablePath) return null;

  const encodedPath = encodeImagePath(optimizablePath);
  return `${IMAGE_OPTIMIZER_BASE_URL}/${Math.floor(options.width)}/${options.format}/${encodedPath}`;
}

export function buildOptimizedSrcSet(
  originalUrl: string,
  format: ImageOptimizerFormat,
): string | null {
  const entries = IMAGE_OPTIMIZER_WIDTHS
    .map((width) => {
      const optimizedUrl = optimizeImageUrl(originalUrl, { width, format });
      return optimizedUrl ? `${optimizedUrl} ${width}w` : null;
    })
    .filter((entry): entry is string => entry !== null);

  return entries.length > 0 ? entries.join(", ") : null;
}

export function convertImgTagToPicture(tag: string): string {
  const src = extractSrc(tag);
  if (!src) return tag;

  const avifSrcSet = buildOptimizedSrcSet(src, "avif");
  const webpSrcSet = buildOptimizedSrcSet(src, "webp");
  const jpegFallback = optimizeImageUrl(src, {
    width: DEFAULT_FALLBACK_WIDTH,
    format: "jpeg",
  });

  if (!avifSrcSet || !webpSrcSet || !jpegFallback) {
    return tag;
  }

  const imgTag = tag.replace(
    /\bsrc\s*=\s*["'][^"']+["']/i,
    `src="${attr(jpegFallback)}"`,
  );

  return `<picture><source srcset="${attr(avifSrcSet)}" type="image/avif"><source srcset="${attr(webpSrcSet)}" type="image/webp">${imgTag}</picture>`;
}

export function maybeOptimizeImageTag(tag: string): string {
  const src = extractSrc(tag);
  if (!src) return tag;

  const optimizablePath = getOptimizableImagePath(src);
  if (!optimizablePath) {
    return tag.replace(/\bsrc\s*=\s*["'][^"']+["']/i, `src="${escapeUrl(src)}"`);
  }

  return convertImgTagToPicture(tag);
}
